import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Label, Modal, ModalBody } from 'reactstrap';
import { tidyPrompt } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import { SECTIONS, EXTRA, extraHeadings } from './sections';
import '../../assets/css/--component-rubric.css';

/**
 * The prompt, written as six labelled boxes instead of one empty one.
 *
 * A blank textarea gets prose, and prose scores zero against a check that
 * looks for sections — which is exactly what happened to the first real prompt
 * anyone typed: it said who the agent was, who called, how to speak and what
 * never to do, and the panel called all five missing. Nagging someone about
 * the shape of their writing is no use. Asking six questions is.
 *
 * So the structure is the interface. The headings the model is trained to
 * weight are the fields the customer fills in, and an unstructured prompt
 * stops being something the editor can produce.
 */

export {
  SECTIONS,
  EXTRA,
  parseSections,
  extraHeadings,
  looksStructured,
  emptySections,
  assemblePrompt,
} from './sections';

/**
 * A box the size of what is in it.
 *
 * Six fixed-height textareas stack into a page of mostly empty space that has
 * to be scrolled past to reach the save button. Most of these answers are a
 * sentence; two are a short list. So each starts at one line and grows as it
 * is typed into, and the whole editor fits on a screen until it has something
 * to show.
 */
const GrowingTextarea = ({ value, onChange, ...rest }) => {
  const ref = useRef(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Collapse first, or the box can only ever get taller.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Also on mount and when filled from elsewhere — the wizard and the paste
  // sorter both write these boxes without anybody typing in them.
  useEffect(fit, [fit, value]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => { onChange(e); fit(); }}
      className="fs-16 rounded p-2 custom-textarea form-control prompt-section-box"
      rows={1}
      {...rest}
    />
  );
};

const PromptSections = ({ values, onChange, errors = {}, onOpenWizard }) => {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasted, setPasted] = useState('');
  const [tidying, setTidying] = useState(false);

  // The bridge for a prompt somebody already has. It sorts their sentences
  // into the boxes without rewording any of them, and says so if anything did
  // not survive the sort.
  const tidy = async () => {
    if (!pasted.trim()) return;
    setTidying(true);
    try {
      const response = await tidyPrompt(pasted);
      const data = response?.data?.data ?? response?.data;
      if (!response?.status || !data?.sections) {
        showToast('error', 'Could not sort that', data?.message || 'Your prompt is unchanged. Please try again.');
        return;
      }
      const leftover = (data.leftover || '').trim();
      // Anything the model could not place keeps riding along, rather than
      // being mentioned in a toast and then quietly discarded.
      onChange({ ...values, ...data.sections, [EXTRA]: [values[EXTRA], leftover].filter(Boolean).join('\n\n') });
      setPasteOpen(false);
      setPasted('');
      const lost = data.droppedSentences || [];
      if (lost.length) {
        showToast('error', `${lost.length} line(s) did not fit`, `Check the boxes — this did not land anywhere: "${lost[0].slice(0, 70)}"`);
      } else if (leftover) {
        showToast('success', 'Sorted, with a leftover', `This fitted no section: "${leftover.slice(0, 70)}"`);
      } else {
        showToast('success', 'Sorted into sections', 'Nothing was reworded. Move anything that landed in the wrong box.');
      }
    } catch {
      showToast('error', 'Could not sort that', 'Your prompt is unchanged. Please try again.');
    } finally {
      setTidying(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Label className="text-left text-black font-bold mb-0">Prompt</Label>
        <div className="d-flex gap-3">
          {onOpenWizard && (
            <Button type="button" color="link" className="p-0 fs-14" onClick={onOpenWizard}>
              Answer questions instead
            </Button>
          )}
          <Button type="button" color="link" className="p-0 fs-14" onClick={() => setPasteOpen(true)}>
            I already have a prompt
          </Button>
        </div>
      </div>

      {SECTIONS.map((section) => (
        <div className="prompt-section" key={section.key}>
          <Label htmlFor={`section-${section.key}`} className="prompt-section-label">
            {section.label}
            {!section.required && <span className="prompt-section-optional"> — optional</span>}
            {/* Beside the label, not under it: six hints on their own lines is
                six lines of the page spent before anything is typed. */}
            <span className="prompt-section-hint">{section.hint}</span>
          </Label>
          <GrowingTextarea
            id={`section-${section.key}`}
            placeholder={section.placeholder}
            value={values[section.key] || ''}
            onChange={(e) => onChange({ ...values, [section.key]: e.target.value })}
          />
          {errors[section.key] && <p className="invalid-feedback d-block">{errors[section.key]}</p>}
        </div>
      ))}

      {/* Said out loud, because the saved prompt will be longer than the boxes
          and nobody should have to wonder whether we threw the rest away. */}
      {extraHeadings(values).length > 0 && (
        <p className="prompt-section-extra">
          Also kept, exactly as written: {extraHeadings(values).join(', ')}. These have no box
          here, but they are part of your prompt and are saved with it.
        </p>
      )}

      <Modal isOpen={pasteOpen} toggle={() => setPasteOpen(false)} centered size="lg">
        <ModalBody className="p-4">
          <h5 className="mb-2">Paste the prompt you have</h5>
          <p className="fs-14 text-muted">
            We will sort your sentences into the boxes above. Nothing is reworded — not even
            spelling — and you can move anything that lands in the wrong place.
          </p>
          <Input
            type="textarea"
            rows="10"
            className="custom-textarea"
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder="Paste it here…"
          />
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="light" onClick={() => setPasteOpen(false)}>Cancel</Button>
            <Button color="primary" disabled={tidying || !pasted.trim()} onClick={tidy}>
              {tidying ? 'Sorting…' : 'Sort it into the boxes'}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PromptSections;
